import React, { useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import LoginAction from "@common/redux/actions/LoginAction";
import useLogin from "@common/components/hooks/useLogin";
import { Button, Header, Label } from "semantic-ui-react";
import ComplianceClient from "@common/api/ComplianceClient";
import useEventDetailsGrid from "../../hooks/useEventDetailsGrid";
import { handleError } from "@common/components/helpers/error";
import { confirmModal } from "@actions/modals";
import useAuthorisation from "@common/components/hooks/useAuthorisation";
import { getFormattedDate } from "../../helpers/dates";

const ComplianceHeader = ({ confirmModal, logIn, title, loadRecord }) => {
  const {
    optionComplianceAllowEdit,
    optionComplianceAllowAuthorise,
    optionComplianceAllowDelete,
  } = logIn;

  const [{ isInReadMode }] = useLogin();
  const {
    activeRow,
    activeRow: {
      isReadOnly,
      id,
      status: { name },
    },
    onRefresh,
  } = useEventDetailsGrid();

  const { maxLength, toggleAuthorisationModal } = useAuthorisation();

  const onDeleteClick = () => {
    confirmModal(
      `Are you sure you wish to delete this compliance record #${id}? `,
      true,
      async () => {
        ComplianceClient.Delete(id).then(
          () => onRefresh(false, true),
          handleError,
        );
      },
      async () => {},
      true,
    );
  };

  return (
    <div className="mb-1">
      <ComplianceTitle
        title={title}
        id={id}
        activeRow={activeRow}
        maxLength={maxLength}
      />

      <Button
        content={isInReadMode ? "Edit" : "Back"}
        compact
        disabled={isReadOnly || !optionComplianceAllowEdit}
        size="small"
        icon={isInReadMode ? "edit" : "arrow left"}
        color={isInReadMode ? "teal" : "teal"}
        onClick={() => {
          LoginAction.dispatch({ isInReadMode: !isInReadMode }).then(() =>
            loadRecord(!isInReadMode),
          );
        }}
      />

      <Button
        content="Authorise"
        color="blue"
        icon="check"
        compact
        disabled={
          isReadOnly || name !== "Auth. Req." || !optionComplianceAllowAuthorise
        }
        onClick={toggleAuthorisationModal}
        size="small"
      />

      <Button
        content="Delete"
        negative
        icon="close"
        compact
        disabled={name === "Processed" || !optionComplianceAllowDelete}
        onClick={onDeleteClick}
        size="small"
      />
    </div>
  );
};

const ComplianceTitle = ({ title, id, activeRow }) => {
  const [showMore, setShowMore] = useState(false);
  const [isTextClamped, setIsTextClamped] = useState(false);
  const textRef = useRef(null);

  const textContent = [
    activeRow?.authorisedDateTime
      ? getFormattedDate(activeRow?.authorisedDateTime)
      : null,
    `Created by ${activeRow?.createdByPerson?.name}`,
    activeRow?.authorisedByPerson
      ? `Authorised by ${activeRow?.authorisedByPerson?.name}`
      : null,
    activeRow?.authorisedComments ? activeRow?.authorisedComments : null,
  ]
    .filter(Boolean)
    .join(" | ");

  useEffect(() => {
    const checkIfTextClamped = () => {
      const element = textRef.current;
      if (element) {
        // Compare scroll height with client height to detect clamping
        const isClamped = element.scrollHeight > element.clientHeight;
        setIsTextClamped(isClamped);
      }
    };

    checkIfTextClamped();

    // Recheck on window resize
    window.addEventListener("resize", checkIfTextClamped);
    return () => window.removeEventListener("resize", checkIfTextClamped);
  }, [textContent]);

  return (
    <Header>
      <Label className="ml-0">#{id}</Label> {title}
      <div
        className="pt-1 text-small"
        style={{ display: "flex", alignItems: "center", gap: "8px" }}
      >
        <span
          ref={textRef}
          style={{
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: showMore ? "none" : 1,
            WebkitBoxOrient: "vertical",
            flex: 1,
          }}
        >
          {textContent}
        </span>
        {isTextClamped && (
          <Button
            basic
            size="mini"
            onClick={() => setShowMore(!showMore)}
            style={{ flexShrink: 0 }}
          >
            <b>{showMore ? "Show less" : "Show more"}</b>
          </Button>
        )}
      </div>
    </Header>
  );
};

const mapStateToProps = (state) => {
  return {
    logIn: state.logIn,
  };
};

export default connect(mapStateToProps, { confirmModal })(ComplianceHeader);
